<script>
import { mapActions } from 'vuex'
import DisposalRecyclingNoteDialog from './_DisposalRecyclingNoteDialog.vue'
import ChooseRecycleDisposalPatternBotton from '@/driver/components/ChooseRecycleDisposalPatternBotton.vue'

export default {
  name: "DisposalRecycling",
  components: {
    DisposalRecyclingNoteDialog,
    ChooseRecycleDisposalPatternBotton,
  },
  props: {
    manifest: {
      type: Object,
      default: () => ({}),
    },
    isFirstField: {
      type: Boolean,
      default: () => false,
    },
    callerType: {
      type: String,
      default: () => null,
    },
    quantityUnits: {
      type: Array,
      default: () => [],
    },
    isMatched: {
      type: Boolean,
      default: () => true,
    },
    // 一括（再資源）専用: 候補取得に渡す3キー（item_id / disposer_id / disposer_location_id /disposer_location_code）。
    // 親で全件一致のときのみ共通値が入り、不一致/未取得のときは null。
    bulkCondition: {
      type: Object,
      default: () => null,
    },
  },
  data() {
    return {
      customMessages: {
        double: '数量は半角数字のみ使用できます'
      },
      plantProcessMethods: [],
      standardQuantityTypes: [
        { value: 'fixed_quantity', text: '確定数量' },
        { value: 'waste_quantity', text: '廃棄物の数量' },
        { value: 'transport_quantity', text: '運搬業者の数量' },
        { value: 'disposal_quantity', text: '処分数量' },
      ],
      selectedRecycleDisposerDisposalMethodForm: null,
      recycleDisposerDisposalMethodDetailsList: [],
      noteDialog: {
        show: false,
        index: null,
        note: '',
      },
      noteDialogBulk: {
        show: false,
        detail: null,
        note: '',
      },
    }
  },
  mounted() {
  },
  computed: {
    /**
     * 処分場処理方法セレクトを操作不可にするか。
     * 従来の登録対象チェック（target_for_registration）に加え、
     * 一括（再資源）で同一チェック不一致（isMatched=false）のときも操作不可にする。
     * @return {boolean}
     */
    isRecycleSelectDisabled () {
      return !this.manifest.target_for_registration || (this.callerType === 'bulk' && !this.isMatched)
    },
    recycleDisposerDisposalMethodConditions () {
      // 3キーの取得元を callerType で切り替える。
      // 一括（再資源）: 親から渡る bulkCondition（一致時の共通3キー / 不一致・未取得時は null）由来。
      //   bulkCondition が null のときは3キーが全て undefined になり、候補取得をしない（個別（再資源）側ガードと同形）。
      // 個別（再資源）: 従来どおり this.manifest 由来（後方互換）。
      // require_final_disposal は両モード共通で isDisposalInfoRequired（manifest 由来）を維持する。
      const source = this.callerType === 'bulk' ? this.bulkCondition : this.manifest
      return {
        item_id: source?.item_id,
        disposer_id: source?.disposer_id,
        disposer_location_id: source?.disposer_location_id,
        disposer_location_code: source?.disposer_location_code,
        require_final_disposal: this.isDisposalInfoRequired,
      }
    },
    /**
     * 処分場処理情報が必須かどうかを判定する
     * 最終報告(last_report)の時のみ必須
     */
    isDisposalInfoRequired () {
      return this.manifest?.disposal_report_type === 'last_report'
    },
    isFinalDisposal () {
      return this.manifest?.disposal_report_type === 'last_report'
    },
    isDisposalInfoPrivateRequired () {
      return this.manifest?.disposal_report_type === 'last_report' && this.manifest?.manifest_type !== 'paper_form'
    },
    /**
     * 重量フィールドのバリデーションルール
     */
    disposalQuantityRules () {
      let baseRules = 'double|max_value:9999999999.999|quantityWithDecimal:10,3'
      // 報告区分＝中間　AND　処分場処理方法が未選択の場合：重量はNULL 又は　0以上
      if (this.manifest?.disposal_report_type === 'middle_report' && this.manifest?.recycle_disposer_disposal_method_id === null) {
        baseRules += '|min_value:0'
      // 報告区分＝最終　AND 処分場処理方法が未選択　AND マニ区分＝紙の場合：重量はNULL 又は　0以上
      } else if (this.manifest?.disposal_report_type === 'last_report' && this.manifest?.manifest_type === 'paper_form' && this.manifest?.recycle_disposer_disposal_method_id === null) {
        baseRules += '|min_value:0'
      //上記以外の場合：重量は0.001以上
      } else {
        baseRules += '|required|min_value:0.001'
      }
      return baseRules;
    },

    disposalQuantityPrivateRules () {
      const baseRules = 'double|min_value:0.001|max_value:9999999999.999|quantityWithDecimal:10,3'
      return `required|${baseRules}`
    },
    /**
     * バリデーションルールが参照する共通データ形式に整形する。
     * 一括は割合（before_disposal_ratio）、個別は重量（before_disposal_weight）を評価対象にする。
     */
    recycleDetailValidationTargetList () {
      return (this.recycleDisposerDisposalMethodDetailsList || []).map((detail) => {
        return {
          level: 1,
          before_disposal_ratio: this.parsePercentToNumber(detail.before_disposal_ratio_input),
          before_disposal_weight: Number(detail.before_disposal_method_kg || 0),
        }
      })
    },
    /**
     * callerType に応じて参照ルールを切り替える。
     * 一括: 01工程割合合計（100%）
     * 個別: 01工程重量合計の範囲（基準重量の 90%〜110%）
     */
    recycleDetailRules () {
      if (this.callerType === 'bulk') {
        return {
          pmFirstBeforeRatioTotal: {
            targetList: this.recycleDetailValidationTargetList,
          },
        }
      }

      return {
        pmFirstBeforeWeightTotalRange: {
          targetList: this.recycleDetailValidationTargetList,
          final_disposal_quantity: this.manifest.disposal_quantity,
        },
      }
    },
    recyclePatternDialogCondition () {
      const condition = this.recycleDisposerDisposalMethodConditions
      return {
        item_id: condition.item_id,
        disposer_id: condition.disposer_id,
        disposer_location_id: condition.disposer_location_id,
        disposer_location_code: condition.disposer_location_code,
        final_disposal: this.manifest?.disposal_report_type === 'last_report',
      }
    },
  },
  watch: {
    /**
     * 品目・処分先・処分場所コードの変更を監視し、処分場処理方法の候補を更新する
     * immediate: コンポーネント作成時に即座に実行
     * deep: オブジェクトの深い変更も検知
     */
    recycleDisposerDisposalMethodConditions: {
      handler () {
        this.refreshPlantProcessMethods()
      },
      immediate: true,
      deep: true,
    },
    /**
     * 処分場処理方法IDの変更を監視し、処理方法の詳細を読み込む
     * immediate: 初期値がある場合は即座に詳細を取得
     */
    'manifest.recycle_disposer_disposal_method_id': {
      handler (newValue, oldValue) {
        this.loadRecycleDisposerDisposalMethodDetails(newValue)
      },
      immediate: true,
    },
    /**
     * 確定数量の変更を監視し、値がゼロの場合は基準重量を「廃棄物の数量」に切り替える
     * immediate: 初期値がゼロの場合も処理を実行
     */
    // 'manifest.conversion_fixed_quantity': {
    //   handler (fixedQuantity) {
    //     if (this.isZeroQuantity(fixedQuantity)) {
    //       this.manifest.disposal_standard_quantity_type = 'waste_quantity'
    //       this.onStandardQuantityChange('waste_quantity')
    //     }
    //   },
    //   immediate: true,
    // },
    /**
     * 基準重量タイプの変更を監視し、対応する重量値をdisposal_quantityに設定する
     * immediate: 初期値がある場合は即座に数量を計算して表示
     */
    'manifest.disposal_standard_quantity_type': {
      handler (newValue) {
        if (newValue) {
          this.onStandardQuantityChange(newValue)
        }
      },
      immediate: true,
    },
    /**
     * 基準重量が「処分数量」の場合、報告数量の変更時に disposal_quantity を再取得する
     */
    'manifest.disposal_report_amount': {
      handler (newValue, oldValue) {
        if (oldValue !== undefined &&
            (this.manifest?.disposal_standard_quantity_type === 'disposal_quantity' ||
            (this.manifest?.disposal_standard_quantity_type === 'fixed_quantity' && this.manifest.quantity_determine_by_disposer === true))) {
          this.onStandardQuantityChange('disposal_quantity')
        }
      },
    },
    /**
     * 基準重量が「処分数量」の場合、報告数量単位の変更時に disposal_quantity を再取得する
     */
    'manifest.disposal_report_amount_unit_id': {
      handler (newValue, oldValue) {
        if (oldValue !== undefined &&
            (this.manifest?.disposal_standard_quantity_type === 'disposal_quantity' ||
            (this.manifest?.disposal_standard_quantity_type === 'fixed_quantity' && this.manifest.quantity_determine_by_disposer === true))) {
          this.onStandardQuantityChange('disposal_quantity')
        }
      },
    },
    /**
     * disposal_quantityの変更を監視し、処分方法kgを自動計算する
     * 複写機能などで値が直接設定される場合も対応
     */
    'manifest.disposal_quantity': {
      handler () {
        this.updateDisposalMethodKgValues()
      },
    },
    /**
     * 報告区分の変更を監視し、処分場処理方法の選択と詳細をリセットする
     * 初期化時（oldValue が undefined）は除く
     */
    'manifest.disposal_report_type': {
      handler (newValue, oldValue) {
        if (oldValue !== undefined) {
          this.$set(this.manifest, 'recycle_disposer_disposal_method_id', null)
        }
      },
    },
  },
  methods: {
    ...mapActions('DriverManifestDisposalReport', {
      fetchDisposalQuantity: 'disposalQuantity',
    }),
    ...mapActions('DriverMasterSearchRecycleDisposerDisposalMethod', {
      fetchRecycleMethods: 'index',
    }),
    ...mapActions('DriverMasterRecycleDisposerDisposalMethod', {
      fetchEdit: 'edit',
    }),

    /**
     * 複写ボタン クリックイベント
     */
    copyFieldButtonClickEvent (property, value) {
      this.$emit('copyField', property, value);
    },

    /**
     * 処分方法kgの値を自動計算して更新する
     */
    updateDisposalMethodKgValues () {
      this.recycleDisposerDisposalMethodDetailsList.forEach((detail, index) => {
        // 処分割合（before_disposal_ratio）をパースして数値化
        const beforeRatio = this.parsePercentToNumber(detail.before_disposal_ratio)

        // 左側：数量 × 処分割合
        const methodKg = this.manifest.disposal_quantity * (beforeRatio / 100)
        if (isNaN(methodKg) || methodKg === 0) {
          this.$set(detail, 'before_disposal_method_kg', null)
        } else {
          this.$set(detail, 'before_disposal_method_kg', this.roundHalfUp(methodKg, 3))
        }

        // 右側も同時に計算
        this.updateDisposalItemKg(index)
      })
    },

    /**
     * 処理後物の種類kgを計算して更新する
     * @param {number} index
     */
    updateDisposalItemKg (index) {
      const detail = this.recycleDisposerDisposalMethodDetailsList[index]
      if (!detail) return

      // 処理後割合（after_disposal_ratio）をパースして数値化
      const afterRatio = this.parsePercentToNumber(detail.after_disposal_ratio)

      // 右側：数量 × 処理後割合
      const itemKg = this.manifest.disposal_quantity * (afterRatio / 100)
      if (isNaN(itemKg) || itemKg === 0) {
        this.$set(detail, 'after_disposal_item_kg', null)
      } else {
        this.$set(detail, 'after_disposal_item_kg', this.roundHalfUp(itemKg, 3))
      }
    },

    /**
     * 基準重量変更イベント.
     */
    onStandardQuantityChange (selectedType) {
      // 処分数量 or 確定数量（処分業者が決定） の場合は、画面の受入量から重量を再取得する
            if (
        selectedType === 'disposal_quantity' ||
        (
          selectedType === 'fixed_quantity' &&
          this.manifest.quantity_determine_by_disposer === true
        )
      ) {

        if (this.callerType === 'bulk') {
          this.$set(this.manifest, 'disposal_quantity', null)
          return
        }

        const params = {
          manifest_id: this.manifest.id,
          disposal_report_amount: this.manifest?.disposal_report_amount,
          disposal_report_amount_unit_id: this.manifest?.disposal_report_amount_unit_id,
        }
        // disposal_quantityを取得して設定する
        this.fetchDisposalQuantity({ params }).then((content) => {
          this.$set(this.manifest, 'disposal_quantity', content?.disposal_quantity)
        }).catch(() => {
          this.$set(this.manifest, 'disposal_quantity', null)
        })
        return
      }

      let convertedKey = null

      if (selectedType === 'fixed_quantity' && this.manifest.quantity_determine_by_disposer != true) {
        convertedKey = 'conversion_fixed_quantity'
      } else if (selectedType === 'waste_quantity') {
        convertedKey = 'conversion_waste_quantity'
      } else if (selectedType === 'transport_quantity') {
        convertedKey = 'conversion_transport_quantity'
      }

      this.$set(this.manifest, 'disposal_quantity',  this.manifest?.[convertedKey])
    },

    isZeroQuantity (value) {
      return Number(value) === 0
    },

    /**
     * 数値をパーセント表記に整える.
     * @param value
     * @return {string}
     */
    toPercent (value) {
      if (value === null || value === undefined || value === '') {
        return ''
      }

      const stringValue = `${value}`
      return stringValue.includes('%') ? stringValue : `${stringValue}%`
    },

    /**disposal_standard_quantity_type
     * 処分場処理方法詳細の表示一覧を生成する.
     * @return {Array}
     */
    buildRecycleDisposerDisposalMethodDetails () {
      const details = this.selectedRecycleDisposerDisposalMethodForm?.recycle_disposer_disposal_method_details
        || []

      return details
        .map((detail, index) => {
          // manifestから既存の値を読み込むか、detailから初期化
          const detailObj = {
            id: detail.id,
            before_disposal_method: detail.recycle_disposal_method_name || '',
            before_disposal_ratio: this.toPercent(detail.before_disposal_ratio ?? 0),
            after_disposal_item: detail.recycle_disposed_item_name || '',
            after_disposal_ratio: this.toPercent(detail.after_disposal_ratio ?? 0),
            final_disposal: detail.final_disposal,
            // 初期値はマスタ生値を数値に正規化（生値が "80.0" 等の文字列でも 80 に揃える）。生値の直挿しにしないこと
            before_disposal_ratio_input: this.parsePercentToNumber(detail.before_disposal_ratio ?? 0),
            after_disposal_ratio_input: this.parsePercentToNumber(detail.after_disposal_ratio ?? 0),
            before_disposal_method_kg: null,
            after_disposal_item_kg: null,
            comment: null,
          }

          return detailObj
        })
    },

    /**
     * 選択された処分場処理方法の詳細を個別取得する.
     * @param recycleDisposerDisposalMethodId
     * @return {Promise<void>}
     */
    loadRecycleDisposerDisposalMethodDetails (recycleDisposerDisposalMethodId) {
      if (!recycleDisposerDisposalMethodId) {
        this.selectedRecycleDisposerDisposalMethodForm = null
        this.recycleDisposerDisposalMethodDetailsList = []
        this.clearDisposalKgValues()
        this.$set(this.manifest, 'after_disposal_details', [])
        return Promise.resolve()
      }

      // 古いkg値と備考を先にクリア（新しいデータを設定する前に確実にクリア）
      this.clearDisposalKgValues()

      return this.fetchEdit({ id: recycleDisposerDisposalMethodId }).then((content) => {
        this.selectedRecycleDisposerDisposalMethodForm = content
        this.recycleDisposerDisposalMethodDetailsList = this.buildRecycleDisposerDisposalMethodDetails()
        this.$set(this.manifest, 'after_disposal_details', this.recycleDisposerDisposalMethodDetailsList)
        // nextTickで確実に全ての変更が反映されてから計算
        this.$nextTick(() => {
          this.updateDisposalMethodKgValues()
        })
      }).catch(() => {
        this.selectedRecycleDisposerDisposalMethodForm = null
        this.recycleDisposerDisposalMethodDetailsList = []
        this.$set(this.manifest, 'after_disposal_details', [])
      })
    },

    /**
     * 処分方法kg値と備考をクリアする
     * 処分場処理方法が変更された際に古いデータを確実に削除するため
     */
    clearDisposalKgValues () {
      this.recycleDisposerDisposalMethodDetailsList = []
      this.$set(this.manifest, 'after_disposal_details', [])
    },

    /**
     * 備考追加ダイアログを開く
     */
    openNoteDialog (index, detail) {
      this.noteDialog.index = index
      this.noteDialog.note = detail.comment || ''
      this.noteDialog.show = true
    },

    /**
     * 備考追加ダイアログから備考を反映する
     */
    applyNote (note) {
      if (this.noteDialog.index !== null) {
        this.$set(this.recycleDisposerDisposalMethodDetailsList[this.noteDialog.index], 'comment', note)
      }
    },

    /**
     * 一括（再資源）: 備考追加ダイアログを開く
     */
    openNoteDialogBulk (detail) {
      this.noteDialogBulk.detail = detail
      this.noteDialogBulk.note = detail.comment_input || ''
      this.noteDialogBulk.show = true
    },

    /**
     * 一括（再資源）: 備考追加ダイアログから備考を反映する
     */
    applyNoteBulk (note) {
      if (this.noteDialogBulk.detail !== null) {
        this.$set(this.noteDialogBulk.detail, 'comment_input', note)
      }
    },

    /**
     * 品目と処分先に応じて処分場処理方法の候補を取得する.
     */
    refreshPlantProcessMethods () {
      const condition = this.recycleDisposerDisposalMethodConditions

      if (this.callerType === 'bulk') {
        // 3キーのいずれかが欠ける → 候補を空にして取得しない（個別（再資源）側ガードと同形）
        if (!condition.item_id || !condition.disposer_id || !condition.disposer_location_code) {
          this.plantProcessMethods = []
          return Promise.resolve()
        }

        const params = {
          item_id: condition.item_id,
          disposer_id: condition.disposer_id,
          disposer_location_code: condition.disposer_location_code,
          final_disposal: false
        }

        // final_disposal=true（報告区分=最終時）の付与は既存挙動を維持
        if (condition.require_final_disposal) {
          params.final_disposal = true
        }

        return this.fetchRecycleMethods(params).then((content) => {
          this.plantProcessMethods = content.recycle_disposer_disposal_methods || []
        }).catch(() => {
          this.plantProcessMethods = []
        })
      }

      if (!condition.item_id || !condition.disposer_id || !condition.disposer_location_code) {
        this.plantProcessMethods = []
        return Promise.resolve()
      }

      const params = {
        item_id: condition.item_id,
        disposer_id: condition.disposer_id,
        disposer_location_code: condition.disposer_location_code,
        final_disposal: false
      }

      if (condition.require_final_disposal) {
        params.final_disposal = true
      }

      return this.fetchRecycleMethods(params).then((content) => {
        this.plantProcessMethods = content.recycle_disposer_disposal_methods || []
      }).catch(() => {
        this.plantProcessMethods = []
      })
    },

    /**
     * 処分場処理方法検索モーダルの選択結果を反映する
     * @param payload {{ pattern: { id: number, type: string } }}
     */
    choseRecycleDisposalPatternCallback (payload) {
      const pattern = payload?.pattern
      if (!pattern || pattern.type !== 'methods') {
        return
      }

      const selectedId = pattern.id
      if (!this.plantProcessMethods.some(method => method.value === selectedId)) {
        this.plantProcessMethods = [
          ...this.plantProcessMethods,
          {
            value: selectedId,
            text: pattern.pattern_name,
            pattern_name: pattern.pattern_name,
          }
        ]
      }
      this.$set(this.manifest, 'recycle_disposer_disposal_method_id', selectedId)
    },
  }
}
</script>

<template>
  <div class="reportLayer">
    <v-row dense class="pb-3">
      <v-col cols="12">
        <h3>処分場処理情報</h3>
      </v-col>
    </v-row>

    <!-- 一括（再資源）: 処分事業者・処分事業場・品目が不一致のとき、操作不可の案内を表示 -->
    <v-row dense v-if="callerType === 'bulk' && !isMatched">
      <v-col cols="12">
        <v-alert type="error" dense outlined class="mb-0">
          処分事業者・処分事業場・品目が一致していないため、処分場処理情報は操作できません。
        </v-alert>
      </v-col>
    </v-row>

    <v-row dense v-if="callerType !== 'bulk'">
      <!-- 基準重量 -->
      <v-col cols="7">
        <div class="field-with-copy">
          <validation-provider
            v-slot="{ errors }"
            :class="{ requiredMark: isDisposalInfoPrivateRequired, 'field-with-copy__control': true }"
            :rules="isDisposalInfoPrivateRequired ? 'required' : ''"
            :disabled="!manifest.target_for_registration"
            :vid="`disposal_standard_quantity_type-${manifest.id}`"
            name="基準重量"
          >
            <v-select
              v-model="manifest.disposal_standard_quantity_type"
              :disabled="!manifest.target_for_registration"
              :error-messages="errors"
              :items="standardQuantityTypes"
              @change="onStandardQuantityChange"
              clearable
              dense
              hide-details="auto"
              label="基準重量"
              outlined
              persistent-placeholder
            >
              <template v-slot:append>
                <!-- <span
                  v-if="isZeroQuantity(manifest.conversion_fixed_quantity)"
                  class="yellow-indicator mt-1 mr-1"
                >●</span> -->
              </template>
            </v-select>
          </validation-provider>
          <div
            v-if="callerType !== 'bulk' && isFirstField"
            class="copy-actions"
          >
            <v-btn
              :disabled="!manifest.target_for_registration"
              color="primary"
              small
              @mousedown.stop.prevent
              @click.stop="copyFieldButtonClickEvent('disposal_standard_quantity_type', manifest.disposal_standard_quantity_type)"
            >
              <v-icon>mdi-chevron-double-down</v-icon>
            </v-btn>
          </div>
        </div>
      </v-col>
      <!-- /基準重量 -->

      <!-- 重量 -->
      <v-col cols="5">
        <div class="field-with-copy">
          <validation-provider
            v-slot="{ errors }"
            :disabled="!manifest.target_for_registration"
            :vid="`disposal_quantity-${manifest.id}`"
            name="重量"
            :custom-messages="customMessages"
            :class="{ requiredMark: isDisposalInfoPrivateRequired, 'field-with-copy__control': true }"
            :rules="disposalQuantityRules"
          >
            <v-text-field
              v-model="manifest.disposal_quantity"
              :disabled="!manifest.target_for_registration"
              :error-messages="errors"
              dense
              hide-details="auto"
              label="重量"
              outlined
              persistent-placeholder
              type="number"
              reverse
              @keydown.69.prevent
              @input="updateDisposalMethodKgValues"
            >
              <template v-slot:prepend-inner>
                <span class="mt-1 mr-1">kg</span>
              </template>
            </v-text-field>
          </validation-provider>
          <div
            v-if="callerType !== 'bulk' && isFirstField"
            class="copy-actions"
          >
            <v-btn
              :disabled="!manifest.target_for_registration"
              color="primary"
              small
              @mousedown.stop.prevent
              @click.stop="copyFieldButtonClickEvent('disposal_quantity', manifest.disposal_quantity)"
            >
              <v-icon>mdi-chevron-double-down</v-icon>
            </v-btn>
          </div>
        </div>
      </v-col>
      <!-- /重量 -->
    </v-row>

    <!-- 一括（再資源）: 基準重量セレクト（種別のみ。数量(kg)欄は出さない） -->
    <v-row dense v-if="callerType === 'bulk'">
      <!-- 基準重量 -->
      <v-col cols="12">
        <validation-provider
          v-slot="{ errors }"
          :class="{ requiredMark: isDisposalInfoRequired }"
          :rules="isDisposalInfoRequired ? 'required' : ''"
          :disabled="isRecycleSelectDisabled"
          vid="disposal_standard_quantity_type_bulk"
          name="基準重量"
        >
          <v-select
            v-model="manifest.disposal_standard_quantity_type"
            :disabled="isRecycleSelectDisabled"
            :error-messages="errors"
            :items="standardQuantityTypes"
            clearable
            dense
            hide-details="auto"
            label="基準重量"
            outlined
            persistent-placeholder
          />
        </validation-provider>
      </v-col>
      <!-- /基準重量 -->
    </v-row>

    <v-row dense>
      <v-col cols="12" class="mb-3">
        <div class="field-with-copy">
          <validation-provider
            v-slot="{ errors }"
            :disabled="isRecycleSelectDisabled"
            :vid="`recycle_disposal_method-${manifest.id}`"
            :class="{ requiredMark: callerType === 'bulk' ? isDisposalInfoRequired : isDisposalInfoPrivateRequired, 'field-with-copy__control': true }"
            :rules="callerType === 'bulk' ? (isDisposalInfoRequired ? 'required' : '') : (isDisposalInfoPrivateRequired ? 'required' : '')"
            name="処分場処理方法"
          >
            <v-select
              v-model="manifest.recycle_disposer_disposal_method_id"
              :disabled="isRecycleSelectDisabled"
              :error-messages="errors"
              :items="plantProcessMethods"
              clearable
              dense
              hide-details="auto"
              label="処分場処理方法"
              outlined
              persistent-placeholder
            >
              <template v-slot:item="{ item, on, attrs }">
                <v-list-item v-on="on" v-bind="attrs">
                  <v-list-item-content>
                    <template>
                      <div class="d-flex align-center flex-wrap py-1" style="gap: 8px;">
                        <v-chip color="primary" label text-color="white" x-small>処分場処理方法</v-chip>
                        <span class="font-weight-medium">{{ item.pattern_name || item.text }}</span>
                      </div>
                    </template>
                  </v-list-item-content>
                </v-list-item>
              </template>
              <template v-slot:selection="{ item }">
                {{ item.pattern_name || item.text }}
              </template>
            </v-select>
          </validation-provider>

          <div class="select-actions">
            <choose-recycle-disposal-pattern-botton
              :disabled="isRecycleSelectDisabled"
              :fixed-condition="recyclePatternDialogCondition"
              small-button
              button-caption="選択"
              only-method
              :final-disposal="isFinalDisposal"
              @choseRecycleDisposalPatternCallback="choseRecycleDisposalPatternCallback"
            />
          </div>

          <div
            v-if="callerType !== 'bulk' && isFirstField"
            class="copy-actions"
          >
            <v-btn
              :disabled="!manifest.target_for_registration"
              color="primary"
              small
              @mousedown.stop.prevent
              @click.stop="copyFieldButtonClickEvent('recycle_disposer_disposal_method_id', manifest.recycle_disposer_disposal_method_id)"
            >
              <v-icon>mdi-chevron-double-down</v-icon>
            </v-btn>
          </div>
        </div>
      </v-col>
      <!-- /処分場処理法 -->
    </v-row>

    <validation-provider
      v-slot="{ errors: detailErrors }"
      name="処分場処理方法詳細"
      :rules="recycleDetailRules"
    >
      <div class="hidden">
        <v-input
          v-model="manifest"
          :error-messages="detailErrors"
          dense
          outlined
          readonly
          hide-details="auto"
        >
          <template v-slot:message>
            <div class="ml-2">
              <p v-for="(message, index) in detailErrors" :key="index">{{ message }}</p>
            </div>
          </template>
        </v-input>
      </div>
    </validation-provider>

    <!-- 処分場処理方法詳細 -->
    <template v-if="recycleDisposerDisposalMethodDetailsList.length > 0">

      <!-- 一括（再資源）: 表示専用。左(処分方法/処理前割合) → 右(処理後物/処理後割合) を枠付きフィールドで表示（readonly） -->
      <template v-if="callerType === 'bulk'">
        <v-row
          v-for="(detail, index) in recycleDisposerDisposalMethodDetailsList"
          :key="`bulk-detail-${index}`"
          dense
          class="mb-2"
        >
          <v-col cols="12">
            <div class="d-flex align-center" style="gap: 8px;">
              <!-- 左フィールド：枠上ラベル=処分方法名 / 枠内=処理前割合(数値のみ) -->
              <v-text-field
                v-model="detail.before_disposal_ratio_input"
                type="number"
                title=""
                dense
                hide-details="auto"
                outlined
                persistent-placeholder
                class="flex-grow-1"
                reverse
                @keydown.69.prevent
              >
                <template v-slot:label>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <span v-bind="attrs" v-on="on">{{ detail.before_disposal_method }}</span>
                    </template>
                    <span>{{ detail.before_disposal_method }}</span>
                  </v-tooltip>
                </template>
                <!-- % は枠の内（append スロット） -->
                <template v-slot:prepend-inner>
                  <span class="mt-1 mr-1">%</span>
                </template>
              </v-text-field>
              <!-- 処理前 → 処理後 -->
              <span class="mx-1">→</span>
              <!-- 右フィールド：枠上ラベル=処理後物名 / 枠内=処理後割合(数値のみ) -->
              <v-text-field
                v-model="detail.after_disposal_ratio_input"
                type="number"
                title=""
                dense
                hide-details="auto"
                outlined
                persistent-placeholder
                class="flex-grow-1"
                reverse
                @keydown.69.prevent
              >
                <template v-slot:label>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <span v-bind="attrs" v-on="on">{{ detail.after_disposal_item }}</span>
                    </template>
                    <span>{{ detail.after_disposal_item }}</span>
                  </v-tooltip>
                </template>
                <!-- % は枠の内（append スロット） -->
                <template v-slot:prepend-inner>
                  <span class="mt-1 mr-1">%</span>
                </template>
              </v-text-field>
              <!-- 備考追加ボタン -->
              <v-btn
                icon
                small
                color="primary"
                @click="openNoteDialogBulk(detail)"
                class="ml-1"
              >
                <v-icon>mdi-file-document-plus-outline</v-icon>
              </v-btn>
            </div>
          </v-col>
        </v-row>
      </template>

      <!-- 個別: kg 入力（矢印付き） -->
      <template v-else>
      <v-row
        v-for="(detail, index) in recycleDisposerDisposalMethodDetailsList"
        :key="`detail-${index}`"
        dense
        class="mb-2"
      >
        <v-col cols="12" class="py-0">
          <div class="d-flex align-start">
            <!-- 左：処分方法 -->
            <validation-provider
              v-slot="{ errors }"
              :disabled="!manifest.target_for_registration"
              :vid="`before_disposal_method_kg-${manifest.id}-${index}`"
              :name="detail.before_disposal_method"
              :rules="disposalQuantityPrivateRules"
              class="flex-grow-1 d-flex flex-column"
            >
              <v-text-field
                v-model="detail.before_disposal_method_kg"
                :disabled="!manifest.target_for_registration"
                title=""
                :error-messages="errors"
                dense
                hide-details="auto"
                outlined
                persistent-placeholder
                type="number"
                reverse
                @keydown.69.prevent
              >
                <template v-slot:label>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <span v-bind="attrs" v-on="on">{{ detail.before_disposal_method }}</span>
                    </template>
                    <span>{{ detail.before_disposal_method }}</span>
                  </v-tooltip>
                </template>
                <template v-slot:prepend-inner>
                  <span class="mt-1 mr-1">kg</span>
                </template>
              </v-text-field>
              <!-- 処分割合をテキストコントロールの下に表示 -->
              <div class="text-caption text-center text--secondary my-0 py-0">
                ({{ detail.before_disposal_ratio }})
              </div>
            </validation-provider>

            <!-- 矢印 -->
            <!-- テキストフィールドの高さと合わせるため、上部にマージンを設定して調整 -->
            <span class="mx-1 mt-2">→</span>

            <!-- 右：処理後物の種類 -->
            <validation-provider
              v-slot="{ errors }"
              :disabled="!manifest.target_for_registration"
              :vid="`after_disposal_item_kg-${manifest.id}-${index}`"
              :name="detail.after_disposal_item"
              :rules="disposalQuantityPrivateRules"
              class="flex-grow-1 d-flex flex-column"
            >
              <v-text-field
                v-model="detail.after_disposal_item_kg"
                :disabled="!manifest.target_for_registration"
                title=""
                :error-messages="errors"
                dense
                hide-details="auto"
                outlined
                persistent-placeholder
                type="number"
                reverse
                @keydown.69.prevent
              >
                <template v-slot:label>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <span v-bind="attrs" v-on="on">{{ detail.after_disposal_item }}</span>
                    </template>
                    <span>{{ detail.after_disposal_item }}</span>
                  </v-tooltip>
                </template>
                <template v-slot:prepend-inner>
                  <span class="mt-1 mr-1">kg</span>
                </template>
              </v-text-field>
              <!-- 処理後割合をテキストコントロールの下に表示 -->
              <div class="text-caption text-center text--secondary my-0 py-0">
                ({{ detail.after_disposal_ratio }})
              </div>
            </validation-provider>

            <!-- 備考追加ボタン -->
            <v-btn
              :disabled="!manifest.target_for_registration"
              icon
              small
              color="primary"
              @click="openNoteDialog(index, detail)"
              class="ml-1"
            >
              <v-icon>mdi-file-document-plus-outline</v-icon>
            </v-btn>
          </div>
        </v-col>
      </v-row>
      </template>
      <!-- 一括登録・個別登録 共通の注釈 -->
      <span style="font-size: 70%">※ 報告区分が「中間」の場合、再資源化の情報はJWNETへは連携されません</span>
    </template>
    <!-- /処分場処理方法詳細 -->

    <!-- 備考追加ダイアログ -->
    <DisposalRecyclingNoteDialog
      v-model="noteDialog.show"
      :note="noteDialog.note"
      @apply="applyNote"
    />

    <!-- 備考追加ダイアログ（一括（再資源）） -->
    <DisposalRecyclingNoteDialog
      v-model="noteDialogBulk.show"
      :note="noteDialogBulk.note"
      @apply="applyNoteBulk"
    />
  </div>
</template>

<style scoped lang="scss">
// outlinedフィールドのラベルに背景色を設定して枠線と重ならないようにする
::v-deep .v-text-field--outlined,
::v-deep .v-select--outlined {
  .v-label {
    background-color: white;
    padding: 0 4px;
  }
  // ダークモードやテーマに応じて背景色を調整する場合
  &.v-input--is-disabled .v-label {
    background-color: #fafafa;
  }
}


// 確定数量が0の場合の黄色いインジケーター
.yellow-indicator {
  color: #ffeb3b;
  font-size: 16px;
  line-height: 1;
}

// 計算値と異なる場合のオレンジ色のインジケーター
//.orange-indicator {
//  color: #FF9800;
//  font-size: 16px;
//  line-height: 1;
//}

.field-with-copy {
  display: flex;
  align-items: flex-start;
}

.field-with-copy__control {
  flex: 1;
  min-width: 0;
}

.copy-actions {
  padding-top: 4px;
}


.select-actions {
  margin-left: 8px;
  padding-top: 4px;
}
</style>
